'use client';

import Image from 'next/image';
import Link from 'next/link';

import Container from '@/components/layouts/container';
import { Button } from '@/components/ui/button';

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
}) {
  const getErrorContent = () => {
    const status = (error as { status?: number }).status;
    const name = (error as { name?: string }).name;

    if (status === 429 || name === 'RateLimitError') {
      return {
        title: 'Rate Limit Exceeded',
        message:
          'You have made too many requests. Please wait a moment and try again.',
      };
    }

    if (status === 401) {
      return {
        title: 'Session Expired',
        message:
          'Your session has expired. You will be redirected to the login page.',
      };
    }

    if (status === 403) {
      return {
        title: 'Access Denied',
        message: 'You do not have permission to access this page.',
      };
    }

    if (status === 404) {
      return {
        title: 'Not Found',
        message: 'The requested resource could not be found.',
      };
    }

    if (status && status >= 500) {
      return {
        title: 'Server Unavailable',
        message:
          'The server is currently experiencing issues. Please try again later.',
      };
    }

    return {
      title: 'Something went wrong',
      message:
        'We encountered an unexpected error. Please try again or contact support if the problem persists.',
    };
  };

  const { title, message } = getErrorContent();

  return (
    <div className='min-h-screen bg-brand-light'>
      <Container className='flex min-h-screen flex-col items-center justify-center py-10'>
        <Link href='/' className='mb-8 flex items-center gap-1'>
          <Image
            src='/logos/skill4skill-logo-icon.svg'
            alt='Skill4Skill Logo'
            width={48}
            height={48}
            style={{ height: '48px', width: '48px' }}
            className='h-12 w-auto'
            priority={true}
          />
          <span className='ml-2 text-xl font-bold text-brand-base'>
            Skill4Skill
          </span>
        </Link>

        <div className='w-full max-w-lg rounded-lg bg-white p-8 text-center shadow-sm'>
          <h1 className='mb-4 font-young text-2xl text-brand-dark'>{title}</h1>
          <p className='mb-6 text-gray-600'>{message}</p>

          <div className='flex justify-center'>
            <Button variant='secondary' asChild>
              <Link href='/'>Return home</Link>
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}
